import {Directive, HostListener, inject, InjectionToken, OnDestroy, OnInit} from '@angular/core';
import {LocalStorageService} from '@xmedical/core';
import {interval, Subject, Subscription} from 'rxjs';

/**
 * Injection token for the idle timeout storage key
 * Apps should provide this when importing the directive
 */
export const IDLE_TIMEOUT_STORAGE_KEY = new InjectionToken<string>('idle.timeout.storage.key');

/**
 * Directive that tracks user idle time and stores the expiration time in local storage.
 * When the user is inactive for the configured timeout period, it triggers an event.
 *
 * The storage key is injectable, allowing different apps to use different keys.
 *
 * Usage:
 * ```html
 * <div appIdleTimeout></div>
 * ```
 *
 * Configuration:
 * ```typescript
 * providers: [
 *   { provide: IDLE_TIMEOUT_STORAGE_KEY, useValue: '_my_app_idle_timeout' }
 * ]
 * ```
 */
@Directive({
  selector: '[xuiIdleTimeout]',
})
export class IdleTimeoutDirective implements OnInit, OnDestroy {
  private localStorage = inject(LocalStorageService);
  private storageKey = inject(IDLE_TIMEOUT_STORAGE_KEY);

  private timerSubscription: Subscription;
  private timeout = 15 * 60 * 1000;
  private debounceTimeout = 1000;
  private eventsSubject: Subject<void> = new Subject();
  private debounceTimer: any = null;

  @HostListener('mousemove')
  @HostListener('keydown')
  @HostListener('click')
  updateExpiredTime() {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.localStorage.setItem(this.storageKey, Date.now() + this.timeout);
    }, this.debounceTimeout);
  }

  @HostListener('visibilitychange')
  onVisibilityChanged() {
    if (document.visibilityState === 'visible') {
      this.start(this.timeout);
    } else {
      this.stop();
    }
  }

  start(timeout: number) {
    this.timeout = timeout;
    this.updateExpiredTime();
    this.startTimer();
  }

  stop() {
    this.timerSubscription?.unsubscribe();
  }

  ngOnInit(): void {

  }

  ngOnDestroy(): void {
    this.stop();
  }

  private startTimer() {
    this.updateExpiredTime();
    this.timerSubscription = interval(5000).subscribe(() => {
      const expiredTime = parseInt(this.localStorage.getItem(this.storageKey, 0), 10);
      if (expiredTime < Date.now()) {
        this.eventsSubject.next();
      }
    });
  }

}
